import {
  useDisclaimers,
  useUpdateDisclaimerCheck,
  useValidateDisclaimer,
} from '@/hooks/queries/disclaimers';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useUserStore } from '@/store/use-user-store';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

const ClientDisclaimer = () => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);
  const { logout } = useAuthQuery();
  const contentRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const hasAcceptedDisclaimer = useUserStore(
    (state) => state.hasAcceptedDisclaimer
  );
  const setHasAcceptedDisclaimer = useUserStore(
    (state) => state.setHasAcceptedDisclaimer
  );
  const { data: disclaimerValidation } = useValidateDisclaimer();
  const { data: disclaimersData } = useDisclaimers();
  const updateDisclaimerCheck = useUpdateDisclaimerCheck();
  const disclaimer = disclaimersData?.results?.[0];
  const policyText = disclaimer?.text?.trim() ?? '';

  useEffect(() => {
    if (!disclaimersData || !disclaimer) return;
    if (!hasAcceptedDisclaimer) {
      setShowDisclaimer(true);
    }
  }, [disclaimersData, hasAcceptedDisclaimer, disclaimer]);

  useEffect(() => {
    if (!showDisclaimer) return;
    hasScrolledRef.current = false;
    setHasScrolledToBottom(false);
  }, [showDisclaimer, policyText]);

  const checkScrollPosition = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollHeight, clientHeight, scrollTop } = content;
    const isScrollable = scrollHeight > clientHeight + 1;

    if (!isScrollable) {
      setHasScrolledToBottom(true);
      return;
    }

    const isAtBottom = scrollHeight - clientHeight - scrollTop <= 2;

    if (isAtBottom) {
      hasScrolledRef.current = true;
      setHasScrolledToBottom(true);
    } else if (!hasScrolledRef.current) {
      setHasScrolledToBottom(false);
    }
  }, []);

  // Attach scroll tracking once the modal is open and content is in the DOM
  useEffect(() => {
    if (!showDisclaimer || !policyText) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | undefined;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const setup = () => {
      if (cancelled) return;

      const content = contentRef.current;
      if (!content) {
        requestAnimationFrame(setup);
        return;
      }

      checkScrollPosition();

      timeoutIds.push(
        ...[100, 400, 800].map((delay) =>
          setTimeout(checkScrollPosition, delay)
        )
      );

      resizeObserver = new ResizeObserver(checkScrollPosition);
      resizeObserver.observe(content);

      const innerContent = content.firstElementChild;
      if (innerContent) {
        resizeObserver.observe(innerContent);
      }
    };

    setup();

    return () => {
      cancelled = true;
      timeoutIds.forEach(clearTimeout);
      resizeObserver?.disconnect();
    };
  }, [showDisclaimer, policyText, checkScrollPosition]);

  const handleAcceptDisclaimer = () => {
    setShowDisclaimer(false);
    setHasAcceptedDisclaimer(true);
  };

  const handleAccept = async () => {
    // it should show everytime
    // so we are persisting the disclaimer acceptance in the user store
    if (!disclaimer) return;
    try {
      // lets leverage storing the disclaimer acceptance in BE for first time
      if (disclaimerValidation && !disclaimerValidation?.disclaimer_accepted) {
        await updateDisclaimerCheck.mutateAsync({
          disclaimer: disclaimer.id,
          is_checked: true,
        });
      }
      handleAcceptDisclaimer();
    } catch (error) {
      console.error('Failed to update disclaimer acceptance:', error);
    }
  };

  const handleDeclineConfirm = async () => {
    try {
      await logout.mutateAsync();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  if (!disclaimer) {
    return null;
  }

  if (hasAcceptedDisclaimer) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={showDisclaimer}
        hideCloseButton
        isDismissable={false}
        size="2xl"
        classNames={{ base: 'max-h-[90vh]' }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {disclaimer.title}
          </ModalHeader>
          <ModalBody>
            <div
              className="h-[60vh] overflow-auto pr-2"
              ref={contentRef}
              onScroll={checkScrollPosition}
            >
              <div className="prose prose-sm max-w-none dark:prose-invert">
                {policyText ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSanitize]}
                  >
                    {policyText}
                  </ReactMarkdown>
                ) : (
                  <p className="text-danger">
                    Policy content is unavailable. Please contact your
                    administrator.
                  </p>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={() => setShowDeclineConfirmation(true)}
            >
              Decline
            </Button>
            <Button
              color="primary"
              onPress={handleAccept}
              isDisabled={!policyText || !hasScrolledToBottom}
            >
              Accept
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Decline Confirmation Modal */}
      <Modal
        isOpen={showDeclineConfirmation}
        onClose={() => setShowDeclineConfirmation(false)}
        size="sm"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Are you sure you want to decline?
          </ModalHeader>
          <ModalBody>
            <p>
              You must accept the disclaimer to continue using the platform.
              This will log you out of the system.
            </p>
          </ModalBody>
          <ModalFooter className="justify-between border-t mt-4">
            <Button
              color="default"
              variant="bordered"
              onPress={() => setShowDeclineConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDeclineConfirm}
              isLoading={logout.isPending}
            >
              Yes, Decline
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ClientDisclaimer;
